import { Injectable } from '@angular/core';
import {
	AbstractCRUDService,
	ExtendableRefBookService
} from '@campaign-portal/components-library';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { UsersAndRolesService } from '@helpers/api/users-and-roles.service';
import {
	PermissionsReadResponse,
	RolesCreateResponse, RolesDeleteRequest, RolesDeleteResponse,
	RolesReadRequest,
	RolesReadResponse, RolesUpdateResponse
} from '@helpers/types/user-and-roles';
import { Permission, Role } from '@helpers/types/models';
import { ExportService } from '@helpers/api/export.service';
import { RolesFieldsService } from './roles.fields.service';
import { exist } from '@campaign-portal/namespace/common/id';
import { ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';

@Injectable({
	providedIn: 'root'
})
export class RolesService extends ExtendableRefBookService<Role<exist>> implements AbstractCRUDService {

	override readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	readonly entity = '';
	readonly title = '';
	readonly create = this.update;
	permissions: string[] = [];

	constructor(
		private readonly usersAndRoles: UsersAndRolesService,
		private readonly fields: RolesFieldsService,
		private readonly exportService: ExportService
	) {
		super();
	}

	override load(): Observable<ReadResponse<Role<number>[]>> {
		return this.usersAndRoles.loader<ReadResponse<Role<number>[]>>('Roles.Read', {}, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: true,
						Total: resp.Total,
						Data: resp.Data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	override process(resp: ReadResponse<Role<number>[]>): ReadResponse<Role<number>[]> {
		this.list$.next(resp.Data);
		this.map$.next(this.list.reduce((result, item) => {
			result.set(item.ID, item);
			return result;
		}, new Map()));
		return resp;
	}

	read(params: RPCRequestParams): Observable<RolesReadResponse> {
		const parsedParams: RolesReadRequest = this.prepareParams(params);
		return this.usersAndRoles.loader<RolesReadResponse>('Roles.Read', parsedParams, this.loading$);
	}

	update(params: Role): Observable<RolesUpdateResponse | RolesCreateResponse> {
		const method = params.ID ? 'Roles.Update' : 'Roles.Create';
		return this.usersAndRoles.loader<RolesUpdateResponse | RolesCreateResponse>(method, params, this.loading$);
	}

	delete(params: RolesDeleteRequest): Observable<RolesDeleteResponse> {
		return this.usersAndRoles.loader<RolesDeleteResponse>('Roles.Delete', params, this.loading$);
	}

	getPermissions(): Observable<PermissionsReadResponse> {
		return this.usersAndRoles.loader<PermissionsReadResponse>('Permissions.Read')
			.pipe(
				tap(res => {
					const data: Permission[] = res?.Data;
					this.permissions = this.flatten(data);
				})
			)
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		const exportParams: RPCRequestParams = {
			...params,
			Paging: { Skip: 0, Take: total }
		};
		return this.exportService.export(
			this.usersAndRoles.userAndRolesApi.url,
			'Roles.Read',
			this.prepareParams(exportParams),
			this.fields.headers,
			'roles'
		);
	}

	flatten(tree: Permission[], groupsOnly?: boolean, groupName?: string): string[] {
		return tree.reduce((r: string[], node) => {
			node.Fullname = (groupName || '') + node.Name;
			if (node.Sub?.length) {
				if (groupsOnly) {
					r.push(node.Fullname);
				}
				r = r.concat(this.flatten(node.Sub, groupsOnly, node.Fullname + '.'));
			} else {
				if (!groupsOnly) {
					r.push(node.Fullname);
				}
			}
			return r;
		}, []);
	}

	private prepareParams(params: RPCRequestParams): RolesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: '',
			direction: '',
			Filters: params.Filters?.flatMap(this.parseFilterField) ?? []
		};
	}
		
	private parseFilterField(filter: Filter): { Type: string, Value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'Name':
				return { Type: 'name', Value: value as string };
			case 'Author':
				return { Type: 'author_login', Value: value as string };
			case 'Permissions':
				return { Type: 'permissions', Value: (value as string[]).join(',') };
		
			default:
				return { Type: variable, Value: value + '' };
		}
	}

}
